import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { TCString } from '@iabtechlabtcf/core'

import { getCookie } from './utils/helpers'

import type { ConsentConfig } from '@xcel-next/types'

type TcfApiWindow = Window & {
  __tcfapi?: (
    command: string,
    version: number,
    callback: (data: unknown, success: boolean) => void,
    ...args: unknown[]
  ) => void
}

type CustomVendorGrant = {
  vendorGrant: boolean,
  purposeGrants: Record<string, boolean>
}
export type CustomVendorGrants = Record<string, CustomVendorGrant>

type CustomVendorConsentsResponse = {
  grants?: Record<string, { vendorGrant?: boolean, purposeGrants?: Record<string, boolean> }>
}

// Sourcepoint's per-vendor grants from getCustomVendorConsents. `purposeGrants` is keyed by this
// endpoint's own purpose ids, which differ per Sourcepoint account (i.e. per environment) and from
// the vendor-purpose-mapping category ids - see rsConfig.consent.thirdPartyContentPurposeId.
const getCustomVendorGrants = (): Promise<CustomVendorGrants> =>
  new Promise(resolve => {
    if (!import.meta.env.SSR && (window as TcfApiWindow).__tcfapi) {
      // https://documentation.sourcepoint.com/web-implementation/sourcepoint-gdpr-and-tcf-v2-support/__tcfapi-getcustomvendorconsents-api
      (window as TcfApiWindow).__tcfapi?.('getCustomVendorConsents', 2, (vendorConsents, success) => {
        const grants = success ? ((vendorConsents as CustomVendorConsentsResponse)?.grants ?? {}) : {}
        resolve(Object.fromEntries(Object.entries(grants).map(([vendorId, grant]) => [vendorId, {
          vendorGrant: !!grant?.vendorGrant,
          purposeGrants: grant?.purposeGrants ?? {}
        }])))
      })
    } else {
      resolve({})
    }
  })

export const useSourcepointStore = defineStore('sourcepoint', () => {
  // State
  const customVendorGrants = ref<CustomVendorGrants>({})
  // Local opt-in for the embed groups (PUR, where the CMP is unavailable). Reactive so grant/revoke
  // update the UI synchronously without a Sourcepoint round-trip. Persisted to a cookie separately.
  const additionalConsent = ref<Record<string, number>>({})
  const consentConfig = ref<Partial<ConsentConfig>>({})
  const consentAction = ref(false)
  const consentString = ref('')

  const consentHasBeenGiven = (): boolean => {
    if (!import.meta.env.SSR) {
      const cookieConsentString = getCookie('euconsent-v2')
      // just checking whether "euconsent-v2" is included in the cookie string is not enough
      // it's there even if all consents have been revoked ("ALLE EINWILLIGUNGEN WIDERRUFEN" button in the datenschutzmanager)
      // but with a (shorter) consent string, yet holding all the data; specifically `purposeConsents` and `vendorConsents`
      if (cookieConsentString) {
        try {
          setConsentString(cookieConsentString)
          const tcModel = TCString.decode(cookieConsentString)
          // if either of the sizes is greater than 0, consent has been given. :empty-bucket-o-consents:
          return tcModel.purposeConsents.size > 0 || tcModel.vendorConsents.size > 0
        } catch (e) {
          console.error(e)
        }
      }
    }
    return false
  }

  // Getters
  const noScripts = computed(() => {
    return !consentsExist.value || consentConfig.value.isConsentWhitelistPage
  })

  const consentsExist = computed(() => {
    // don't check for consent on the server.
    if (import.meta.env.SSR) return false
    return consentAction.value || consentHasBeenGiven()
  })

  const setCustomVendorGrants = (payload: CustomVendorGrants | null): void => {
    customVendorGrants.value = payload || {}
  }

  const setAdditionalConsent = (payload: Record<string, number> | null): void => {
    additionalConsent.value = payload || {}
  }

  const setConsentConfig = (payload: ConsentConfig | null): void => {
    consentConfig.value = payload ?? {}
  }

  const setConsentAction = (hasConsented: boolean): void => {
    consentAction.value = hasConsented
  }

  const setConsentString = (payload: string | null): void => {
    consentString.value = payload || ''
  }

  const refreshCustomVendorConsents = async (): Promise<void> => {
    setCustomVendorGrants(await getCustomVendorGrants())
  }

  const postCustomConsent = async ({ vendorIds, purposeIds, legitimateInterestIds }: {
    vendorIds: string[],
    purposeIds: string[],
    legitimateInterestIds: string[]
  }): Promise<void> => {
    await new Promise(resolve => {
      if (!import.meta.env.SSR && (window as TcfApiWindow).__tcfapi) {
        // https://documentation.sourcepoint.com/web-implementation/sourcepoint-gdpr-and-tcf-v2-support/__tcfapi-postcustomconsent-api
        (window as TcfApiWindow).__tcfapi?.('postCustomConsent', 2, (_, success) => resolve(success), vendorIds, purposeIds, legitimateInterestIds)
      } else {
        resolve(false)
      }
    })
    await refreshCustomVendorConsents()
  }

  const consentActionPerformed = async (): Promise<void> => {
    await refreshCustomVendorConsents()
    setConsentAction(consentHasBeenGiven())
  }

  return {
    consentConfig,
    consentString,
    noScripts,
    consentsExist,
    customVendorGrants,
    additionalConsent,
    setAdditionalConsent,
    setConsentConfig,
    setConsentString,
    refreshCustomVendorConsents,
    postCustomConsent,
    consentActionPerformed
  }
})
